import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt, faSave, faSync, faCheckSquare,
    faSquare, faDatabase, faLink, faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * ProfilePermissions — Permission matrix: Profile × (Menu Item | Module).
 * Select a profile → see all menu items → toggle CanView / CanEdit / CanDelete.
 * Now uses ItemId to disambiguate modules assigned to multiple groups.
 */
const ProfilePermissions = () => {
    const [profiles,   setProfiles]   = useState([]);
    const [modules,    setModules]    = useState([]);
    const [groups,     setGroups]     = useState([]);
    const [menuItems,  setMenuItems]  = useState([]);
    const [selProfile, setSelProfile] = useState('');
    const [matrix,     setMatrix]     = useState({}); // { [uniqueKey]: { CanView, CanEdit, CanDelete, ModuleId, ItemId } }
    const [loading,    setLoading]    = useState(false);
    const [saving,     setSaving]     = useState(false);
    const [dirty,      setDirty]      = useState(false);

    useEffect(() => {
        request.Get(API_BASE + 'superadmin/profiles',   {}, r => { if (r.success) setProfiles(r.data  || []); });
        request.Get(API_BASE + 'superadmin/modules',    {}, r => { if (r.success) setModules(r.data   || []); });
        request.Get(API_BASE + 'superadmin/groups',     {}, r => { if (r.success) setGroups(r.data    || []); });
        request.Get(API_BASE + 'superadmin/menu-items', {}, r => { if (r.success) setMenuItems(r.data || []); });
    }, []);

    useEffect(() => {
        if (!selProfile) return;
        setLoading(true);
        setDirty(false);
        request.Get(API_BASE + `superadmin/profile-permissions/${selProfile}`, {}, r => {
            if (r.success) {
                const m = {};
                const permissions = r.data || [];

                // 1. Initialize matrix for all menu items
                menuItems.forEach(item => {
                    const key = `item_${item.ItemId}`;
                    m[key] = { CanView: 0, CanEdit: 0, CanDelete: 0, ModuleId: item.ModuleId, ItemId: item.ItemId };
                });

                // 2. Initialize matrix for modules NOT in any group (orphan modules)
                const assignedModuleIds = new Set(menuItems.map(i => i.ModuleId));
                modules.forEach(mod => {
                    if (!assignedModuleIds.has(mod.ModuleId)) {
                        const key = `mod_${mod.ModuleId}`;
                        m[key] = { CanView: 0, CanEdit: 0, CanDelete: 0, ModuleId: mod.ModuleId, ItemId: null };
                    }
                });

                // 3. Overlay actual permissions from backend
                permissions.forEach(perm => {
                    // Try to match by ItemId first (the new way)
                    if (perm.ItemId) {
                        const key = `item_${perm.ItemId}`;
                        if (m[key]) {
                            m[key] = { ...m[key], CanView: perm.CanView, CanEdit: perm.CanEdit, CanDelete: perm.CanDelete };
                        }
                    } else {
                        // Fallback or for modules without ItemId
                        const key = `mod_${perm.ModuleId}`;
                        if (m[key]) {
                            m[key] = { ...m[key], CanView: perm.CanView, CanEdit: perm.CanEdit, CanDelete: perm.CanDelete };
                        } else {
                            // If it's assigned to a module that IS in groups but the permission came without ItemId (legacy)
                            // we might want to map it to the first item found or just leave it.
                            // For now, let's keep it simple.
                        }
                    }
                });
                setMatrix(m);
            } else {
                Notify({ type: 'fail', text: 'Error al cargar permisos' });
            }
            setLoading(false);
        }, 'json', () => setLoading(false));
    }, [selProfile, menuItems, modules]);

    const toggle = (key, field) => {
        setMatrix(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: prev[key]?.[field] ? 0 : 1
            }
        }));
        setDirty(true);
    };

    const selectAll = () => {
        const m = { ...matrix };
        Object.keys(m).forEach(key => {
            m[key] = { ...m[key], CanView: 1, CanEdit: 1, CanDelete: 1 };
        });
        setMatrix(m);
        setDirty(true);
    };

    const clearAll = () => {
        const m = { ...matrix };
        Object.keys(m).forEach(key => {
            m[key] = { ...m[key], CanView: 0, CanEdit: 0, CanDelete: 0 };
        });
        setMatrix(m);
        setDirty(true);
    };

    const handleSave = () => {
        setSaving(true);
        const permissions = Object.values(matrix).map(p => ({
            ModuleId:  p.ModuleId,
            ItemId:    p.ItemId,
            CanView:   p.CanView,
            CanEdit:   p.CanEdit,
            CanDelete: p.CanDelete
        }));

        request.Post(API_BASE + 'superadmin/profile-permissions', {
            profileId: selProfile, permissions
        }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Permisos guardados correctamente' });
                setDirty(false);
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al guardar permisos' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const PermCell = ({ matrixKey, field, label }) => {
        const checked = matrix[matrixKey]?.[field] === 1;
        return (
            <td style={{ textAlign: 'center' }}>
                <button
                    onClick={() => toggle(matrixKey, field)}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                        color: checked
                            ? field === 'CanView' ? '#a855f7' : field === 'CanEdit' ? '#f59e0b' : '#ef4444'
                            : 'rgba(255,255,255,0.1)',
                        fontSize: '1.1rem',
                        transition: 'all 0.15s'
                    }}
                    title={`${label}: ${checked ? 'Quitar' : 'Dar'}`}
                >
                    <FontAwesomeIcon icon={checked ? faCheckSquare : faSquare} />
                </button>
            </td>
        );
    };

    // Prepare rows
    const rows = [];
    menuItems.forEach(item => {
        const mod = modules.find(m => m.ModuleId === item.ModuleId);
        const grp = groups.find(g => g.GroupId === item.GroupId);
        rows.push({
            key: `item_${item.ItemId}`,
            groupLabel: grp?.GroupLabel || '?',
            moduleLabel: mod?.ModuleLabel || '?',
            moduleKey: mod?.ModuleKey || '?',
            moduleType: mod?.ModuleType || '?',
            active: mod?.ModuleActive,
            itemId: item.ItemId,
            moduleId: item.ModuleId
        });
    });

    const assignedModuleIds = new Set(menuItems.map(i => i.ModuleId));
    modules.forEach(mod => {
        if (!assignedModuleIds.has(mod.ModuleId)) {
            rows.push({
                key: `mod_${mod.ModuleId}`,
                groupLabel: '(Sin Grupo)',
                moduleLabel: mod.ModuleLabel,
                moduleKey: mod.ModuleKey,
                moduleType: mod.ModuleType,
                active: mod.ModuleActive,
                itemId: null,
                moduleId: mod.ModuleId
            });
        }
    });

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="d-flex gap-3 align-items-center flex-wrap" style={{ flex: 1 }}>
                    <select
                        className="sa-input"
                        style={{ maxWidth: 280 }}
                        value={selProfile}
                        onChange={e => setSelProfile(e.target.value)}
                    >
                        <option value="">— Seleccioná un Perfil —</option>
                        {profiles.map(p => (
                            <option key={p.ProfileId} value={p.ProfileId}>{p.ProfileName}</option>
                        ))}
                    </select>
                    {selProfile && (
                        <>
                            <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={selectAll}>Marcar todo</button>
                            <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={clearAll}>Limpiar todo</button>
                        </>
                    )}
                </div>
                {dirty && (
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar Permisos</>}
                    </button>
                )}
            </div>

            {!selProfile ? (
                <div className="sa-empty">
                    <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '3rem', opacity: 0.1, display: 'block', margin: '0 auto 1rem' }} />
                    <p style={{ color: '#475569' }}>Seleccioná un perfil para gestionar sus permisos</p>
                </div>
            ) : loading ? (
                <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
            ) : (
                <div className="sa-panel">
                    <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <div className="d-flex gap-4 flex-wrap" style={{ fontSize: '0.75rem' }}>
                            <span style={{ color: '#a855f7', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Ver
                            </span>
                            <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Editar
                            </span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Eliminar
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Grupo</th>
                                    <th>Módulo</th>
                                    <th>Key</th>
                                    <th style={{ textAlign: 'center', color: '#a855f7' }}>Ver</th>
                                    <th style={{ textAlign: 'center', color: '#f59e0b' }}>Editar</th>
                                    <th style={{ textAlign: 'center', color: '#ef4444' }}>Eliminar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={6} className="text-center py-4 text-muted">Sin elementos configurados.</td></tr>
                                )}
                                {rows.map(row => (
                                    <tr key={row.key}>
                                        <td style={{ color: row.itemId ? '#94a3b8' : '#475569', fontSize: '0.8rem' }}>
                                            <FontAwesomeIcon icon={faLayerGroup} className="me-2" style={{ opacity: 0.5 }} />
                                            {row.groupLabel}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 7,
                                                    background: row.active ? 'rgba(168,85,247,0.1)' : 'rgba(255,255,255,0.03)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: row.active ? 'var(--sa-primary)' : '#334155', fontSize: '0.7rem'
                                                }}>
                                                    <FontAwesomeIcon icon={row.moduleType === 'DYNAMIC' ? faDatabase : faLink} />
                                                </div>
                                                <span style={{ fontWeight: 600, fontSize: '0.88rem', color: row.active ? '#f1f5f9' : '#475569' }}>
                                                    {row.moduleLabel}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <code style={{ color: '#64748b', fontSize: '0.72rem' }}>{row.moduleKey}</code>
                                        </td>
                                        <PermCell matrixKey={row.key} field="CanView"   label="Ver" />
                                        <PermCell matrixKey={row.key} field="CanEdit"   label="Editar" />
                                        <PermCell matrixKey={row.key} field="CanDelete" label="Eliminar" />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {dirty && (
                <div style={{
                    position: 'fixed', bottom: '2rem', right: '2rem',
                    background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(168,85,247,0.3)', borderRadius: 14,
                    padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 1000
                }}>
                    <span style={{ fontSize: '0.82rem', color: '#f59e0b' }}>
                        ⚠ Tenés cambios sin guardar
                    </span>
                    <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar</>}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProfilePermissions;
